import apiClient, { extractData } from "./client";

/**
 * Organizational hierarchy API (`directManagerId`).
 * Mirrors the existing Employee.managerId chain without changing
 * the project's current role-based hierarchy rules.
 */

export async function getOrganizationalHierarchyTree() {
  const response = await apiClient.get("/organizational-hierarchy/tree");
  return extractData(response);
}

export async function getDirectManager(employeeId) {
  const response = await apiClient.get(
    `/organizational-hierarchy/${employeeId}/direct-manager`,
  );
  return extractData(response);
}

export async function getSubordinates(employeeId) {
  const response = await apiClient.get(
    `/organizational-hierarchy/${employeeId}/subordinates`,
  );
  return extractData(response);
}

export async function getEmployeeHierarchyChain(employeeId) {
  const response = await apiClient.get(
    `/organizational-hierarchy/${employeeId}/chain`,
  );
  return extractData(response);
}

export async function assignOrganizationalManager(employeeId, managerId) {
  const response = await apiClient.post(
    `/organizational-hierarchy/${employeeId}/manager`,
    { managerId },
  );
  return extractData(response);
}

export async function changeOrganizationalManager(employeeId, newManagerId) {
  const response = await apiClient.patch(
    `/organizational-hierarchy/${employeeId}/manager`,
    { newManagerId },
  );
  return extractData(response);
}

export async function removeOrganizationalManager(employeeId) {
  const response = await apiClient.delete(
    `/organizational-hierarchy/${employeeId}/manager`,
  );
  return extractData(response);
}

/**
 * Keep `directManagerId` aligned with the existing `managerId` selection.
 * Does not change who the manager is — only syncs the hierarchy field.
 *
 * @param {string} employeeId
 * @param {string|null|undefined} managerId Existing hierarchy manager (Employee.managerId)
 * @returns {Promise<object|null>}
 */
export async function syncEmployeeDirectManager(employeeId, managerId) {
  if (!employeeId) return null;

  const nextId = managerId || null;
  let current = null;

  try {
    current = await getDirectManager(employeeId);
  } catch {
    current = null;
  }

  const currentId = current?.id ?? null;

  if (!nextId) {
    if (!currentId) return null;
    return removeOrganizationalManager(employeeId);
  }

  if (!currentId) {
    return assignOrganizationalManager(employeeId, nextId);
  }

  if (currentId === nextId) {
    return current;
  }

  return changeOrganizationalManager(employeeId, nextId);
}

/**
 * Best-effort sync — never blocks employee create/update on hierarchy API errors.
 */
export async function syncEmployeeDirectManagerQuietly(employeeId, managerId) {
  try {
    return await syncEmployeeDirectManager(employeeId, managerId);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[organizationalHierarchy] failed to sync directManagerId",
        err?.response?.data || err?.message || err,
      );
    }
    return null;
  }
}
